/*
 * AI Recommendations in lanes (owner 10-06: variant B1 "quiet lanes", then "a
 * bit more colour - to draw the eye"; recs-redesign/lanes). Pure: no React.
 *
 *   lanes   Act now (critical + overdue) / Due (due) / Consider (consider +
 *           risk), in that order; inside a lane the server's order is kept.
 *   lead    each row starts with the record's own value: the analyte, its
 *           value, ↑ / ↓ and its date, read from the item's evidence[] (the
 *           server's records), NEVER from the model's headline or sentence. No
 *           numeric record: the record's name and date; no record at all:
 *           nothing (an invented number is worse than none).
 *   trim    a served headline that restates the lead ("UACR 48 ↑ (Aug 2025) →
 *           recheck" after "UACR 48 ↑ · Aug 2025") loses the repeated part;
 *           only text is ever removed, never added.
 *   kind    lab / screening / med / risk / vaccine (else "other") from the
 *           item's key, order type and records: the small icon at level 3.
 *   colour  how much colour the lanes carry: one attribute on the drawer,
 *           data-colour 1 / 2 / 3 (CareIQTabN3.css), COLOUR_LEVEL by default.
 */
import { shortDate } from "./recsModel";
import { recordDate } from "./sourceLinks";

/* ───────── colour level ───────── */

// The ONE constant for the default: 1 quiet (B1 as mocked), 2 accent, 3 vivid.
export const COLOUR_LEVEL = 2;
export const COLOUR_LEVELS = [1, 2, 3];
// A per-browser override for side-by-side review on a preview (never needed in use).
export const COLOUR_KEY = "careiq.n3.recsColour";

export const colourLevel = () => {
  try {
    const v = Number(window.localStorage.getItem(COLOUR_KEY));
    if (COLOUR_LEVELS.includes(v)) return v;
  } catch (_) {
    // storage blocked: the default
  }
  return COLOUR_LEVEL;
};

/* ───────── lanes ───────── */

export const LANES = [
  { id: "now", name: "Act now" },
  { id: "due", name: "Due" },
  { id: "con", name: "Consider" },
];
const LANE_OF = { critical: "now", overdue: "now", due: "due", consider: "con", risk: "con" };
export const laneOf = (priority) => LANE_OF[priority] || "con";
export const laneName = (id) => (LANES.find((l) => l.id === id) || LANES[2]).name;

// [{lane, items}] for the lanes that have items, in lane order (the items keep their order).
export const groupByLane = (items) => LANES
  .map((l) => ({ lane: l, items: (items || []).filter((r) => r && laneOf(r.priority) === l.id) }))
  .filter((g) => g.items.length);

// The items in lane order (the band's top item is the first of them).
export const laneOrder = (items) => groupByLane(items).flatMap((g) => g.items);

// The band's "●1 ■3 ○1": the hidden items counted by lane (lanes with none left out).
export const laneCounts = (list) => LANES
  .map((l) => ({ lane: l.id, n: (list || []).filter((r) => r && laneOf(r.priority) === l.id).length }))
  .filter((c) => c.n);

const LANE_WORD = { now: "act now", due: "due", con: "consider" };
export const laneCountsLabel = (list) => {
  const c = laneCounts(list);
  const total = c.reduce((a, x) => a + x.n, 0);
  if (!total) return "Show all: nothing else open";
  return `Show all, ${total} more: ${c.map((x) => `${x.n} ${LANE_WORD[x.lane]}`).join(", ")}`;
};

/* ───────── the value lead ───────── */

const str = (v) => (v == null ? "" : String(v).trim());
const norm = (s) => str(s).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Clinician shorthand for common analytes (deterministic; the record's full name stays in the tooltip).
const LAB_SHORT = {
  LDL: ["ldl", "ldl c", "ldl cholesterol", "ldl cholesterol calc", "ldl calc", "ldl calculated", "ldl direct", "ldl cholesterol direct"],
  HDL: ["hdl", "hdl c", "hdl cholesterol"],
  TC: ["total cholesterol", "cholesterol total", "cholesterol"],
  TG: ["triglycerides", "triglyceride"],
  A1c: ["hemoglobin a1c", "hgb a1c", "hba1c", "a1c", "glycated hemoglobin", "glycohemoglobin"],
  UACR: ["urine albumin creatinine ratio", "albumin creatinine ratio", "microalbumin creatinine ratio", "urine microalbumin creatinine ratio", "uacr"],
  K: ["potassium", "k"],
  Na: ["sodium", "na"],
  Cl: ["chloride"],
  Cr: ["creatinine", "creatinine serum"],
  BUN: ["bun", "blood urea nitrogen", "urea nitrogen"],
  eGFR: ["egfr", "estimated gfr", "gfr estimated", "egfr ckd epi", "egfr non afr american", "gfr"],
  Hgb: ["hemoglobin", "hgb"],
  Hct: ["hematocrit", "hct"],
  Plt: ["platelets", "platelet count", "plt"],
  WBC: ["wbc", "white blood cell count", "white blood cells"],
  TSH: ["tsh", "thyroid stimulating hormone"],
  FT4: ["free t4", "t4 free", "thyroxine free"],
  "Vit D": ["vitamin d 25 hydroxy", "25 hydroxy vitamin d", "vitamin d", "vitamin d 25 oh total"],
  B12: ["vitamin b12", "b12"],
  ALT: ["alt", "alanine aminotransferase"],
  AST: ["ast", "aspartate aminotransferase"],
  ALP: ["alkaline phosphatase"],
  Mg: ["magnesium"],
  Ca: ["calcium"],
  PSA: ["psa", "prostate specific antigen"],
  INR: ["inr"],
};
const SHORT_OF = new Map(Object.entries(LAB_SHORT).flatMap(([s, names]) => names.map((n) => [n, s])));

export const labShort = (name) => SHORT_OF.get(norm(name)) || str(name);
// The names a headline may use for the same analyte (its shorthand, its synonyms, the record's own name).
const labAliases = (name) => {
  const s = labShort(name);
  const list = [str(name), s, ...(LAB_SHORT[s] || [])];
  return [...new Set(list.map(str).filter(Boolean))];
};

// A measurement inside a document's name ("Echo EF 35%", "Nodule 1.2 cm"): a document's title can be
// its AI label (the Documents tab's, CONTRACT §18.3), so a value in it is model text and never leads.
// Names with digits stay ("12-lead ECG", "PCV20", "COVID-19", "2D echo").
const DOC_MEASURE = /\s*[<>]?\d+(?:[.,]\d+)?\s*(?:%|mm\s*hg|mmhg|bpm|mg\/dl|mmol\/l|g\/dl|ng\/ml|ml|mm|cm|kg|lbs?)(?![A-Za-z])/gi;
// A record's name, shortened only where the shorthand is standard.
const docShort = (title) => str(title)
  .replace(DOC_MEASURE, "")
  .replace(/\bultrasound\b/i, "US")
  .replace(/\bechocardiogram\b/i, "Echo")
  .replace(/\belectrocardiogram\b/i, "ECG")
  .replace(/\s+report$/i, "")
  .trim();

// "2026-09-24" -> "Sep 24" in the visit's year, "Mar 2024" otherwise.
export const leadDate = (ymd, refYear, fallback = "") => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str(ymd));
  if (!m) return str(fallback);
  if (refYear && Number(m[1]) !== Number(refYear)) return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  return shortDate(m[0], m[1]);
};

const flagDir = (flag) => {
  const f = norm(flag);
  if (!f || /^n(ormal)?$/.test(f)) return null;
  if (/^(h|hh|high|critical high|crit high|panic high)\b/.test(f) || /\bhigh\b/.test(f)) return "high";
  if (/^(l|ll|low|critical low|crit low|panic low)\b/.test(f) || /\blow\b/.test(f)) return "low";
  return "abnormal";
};

/*
 * One served value segment, "<test> <value> <units>" + " (H)" / " (L)" / " (A)" (CONTRACT §18.3,
 * evidence.py), split into {name, value, units, flag, text}; value "" when it holds no number the
 * split can trust. Test names and units may carry digits ("Vitamin D, 25-Hydroxy 18 ng/mL",
 * "eGFR CKD-EPI 2021 58 mL/min/1.73m2"), so:
 *   flag   only a trailing (H) / (L) / (A) (or a word saying high / low / abnormal / normal); any
 *          other parenthesis ("(calc)") is part of the units, never an abnormal cue
 *   name   the longest of the record's own test names (open.ref.tests, its title) the segment
 *          starts with; else the text before the first free-standing number (not "25-Hydroxy",
 *          "1:160", "10*3"), and a second free-standing number after it makes the split
 *          ambiguous: no value (the record's name shows instead of a wrong number)
 */
const FLAG_WORD = /^(?:h|hh|l|ll|a|aa|n|high|low|abn|abnl|abnormal|normal|(?:critical|crit|panic)(?:\s+(?:high|low))?)$/i;
const NUM = "([<>]=?\\s*)?(-?\\d[\\d,]*(?:\\.\\d+)?)(?![\\w.,:/*^-])";
const LEAD_NUM = new RegExp(`^${NUM}\\s*(.*)$`);
const FREE_NUM = new RegExp(`(?:^|\\s)${NUM}`);
const SPLIT_NUM = new RegExp(`^(.*?\\S)\\s+(${NUM.replace(/\((?!\?)/g, "(?:")}.*)$`);
export const parseLabValue = (seg, names = []) => {
  const text = str(seg);
  let s = text;
  let flag = "";
  const fm = /\s*\(([^()]*)\)\s*$/.exec(s);
  if (fm && FLAG_WORD.test(fm[1].trim())) {
    flag = fm[1].trim();
    s = s.slice(0, fm.index).trim();
  }
  const none = { name: s, value: "", units: "", flag, text };
  const low = s.toLowerCase();
  const known = [...new Set((names || []).map(str).filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    .find((n) => low.startsWith(n.toLowerCase()) && /\s/.test(s.charAt(n.length)));
  let name;
  let rest;
  if (known) {
    name = s.slice(0, known.length);
    rest = s.slice(known.length).trim();
  } else {
    const m = SPLIT_NUM.exec(s);
    if (!m) return none;
    name = m[1];
    rest = m[2];
  }
  const v = LEAD_NUM.exec(rest);
  if (!v) return { ...none, name };
  const units = str(v[3]);
  if (!known && FREE_NUM.test(units)) return none;
  return { name, value: `${(v[1] || "").replace(/\s+/g, "")}${v[2]}`, units, flag, text };
};

// The critical lab results the server named (Follow up's critical rows): [{day, names}].
export const criticalLabs = (body) => ((body && body.followup && Array.isArray(body.followup.rows)) ? body.followup.rows : [])
  .filter((r) => r && r.kind === "fu_lab" && r.group === "critical" && str(r.date))
  .map((r) => ({ day: str(r.date).slice(0, 10), names: [...new Set(labAliases(r.text).map(norm))] }));

const sameAnalyte = (a, names) => {
  const mine = labAliases(a).map(norm);
  return mine.some((n) => n && names.includes(n));
};

// Is this record's value one the server called critical? Only a Follow up critical row (the
// server's critical tiers: the lab's own critical flag or the panic table) of the same analyte on
// the same result date. A `critical_row` source is NOT one: CareIQ's "critical alerts" are
// abnormal values (CONTRACT §12 items 1-2: "abnormal values ... not critical values", attached only
// when the row is not already a Follow up row), so it never makes a value critical.
const isCritical = (ev, name, critLabs) => {
  const day = recordDate(ev).slice(0, 10);
  return (critLabs || []).some((c) => c.day === day && sameAnalyte(name, c.names));
};

/*
 * The lead of one item, or null (nothing structured to show):
 *   {kind: "value", name, full, value, units, dir: high | low | abnormal | null,
 *    sev: critical | abnormal | null, date, tip}
 *   {kind: "name", name, full, date, tip}
 * leadEvidence: the evidence item that lead was read from (the record a click on the lead opens,
 * RecsDrawer.jsx Lead; owner 10-07 "maybe we make the reading clickable?"), or null.
 */
const leadOf = (rec, { refYear = null, critLabs = [] } = {}) => {
  const evs = (rec && Array.isArray(rec.evidence) ? rec.evidence : []).filter((e) => e && typeof e === "object");
  if (!evs.length) return null;
  const lab = evs.find((e) => e.kind === "lab" && str(e.value));
  if (lab) {
    const ref = lab.open && lab.open.ref;
    const first = parseLabValue(str(lab.value).split(" · ")[0], [...(ref && Array.isArray(ref.tests) ? ref.tests : []), lab.title]);
    if (first.value) {
      const dir = flagDir(first.flag);
      // the date shown is the date of the record the lead opens (sourceLinks.js recordDate: one field)
      const date = leadDate(recordDate(lab), refYear, lab.when);
      return {
        ev: lab,
        lead: {
          kind: "value",
          name: labShort(first.name),
          full: first.name,
          value: first.value,
          units: first.units,
          dir,
          sev: dir ? (isCritical(lab, first.name, critLabs) ? "critical" : "abnormal") : null,
          date,
          tip: [first.text, str(lab.when) || date, str(lab.title)].filter(Boolean).join(" · "),
        },
      };
    }
  }
  const e = evs[0];
  const full = str(e.title);
  const name = e.kind === "lab" ? labShort(full) : docShort(full);
  const date = leadDate(recordDate(e), refYear, e.when);
  if (!name && !date) return null;
  return { ev: e, lead: { kind: "name", name, full, date, tip: [full, str(e.when) || date].filter(Boolean).join(" · ") } };
};
export const recLead = (rec, opts) => { const o = leadOf(rec, opts); return o ? o.lead : null; };
export const leadEvidence = (rec, opts) => { const o = leadOf(rec, opts); return o ? o.ev : null; };

// What a screen reader hears after the value: "high", "critical high", "abnormal".
export const leadSr = (lead) => {
  if (!lead || lead.kind !== "value" || !lead.dir) return "";
  if (lead.sev === "critical") return lead.dir === "abnormal" ? "critical" : `critical ${lead.dir}`;
  return lead.dir;
};
// The cue drawn after the value (never colour alone): ↑ / ↓, doubled when critical; a word
// without a direction ("abnl", "crit").
export const leadArrow = (lead) => {
  if (!lead || lead.kind !== "value" || !lead.dir) return "";
  if (lead.dir === "abnormal") return lead.sev === "critical" ? "crit" : "abnl";
  const a = lead.dir === "high" ? "↑" : "↓";
  return lead.sev === "critical" ? a + a : a;
};

/* ───────── the headline without what the lead already says ───────── */

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// a parenthesised date may carry up to two words before it ("(last Mar 2024)", "(CT Aug 22)"), but
// not ones that make it a different date ("(since Sep 11)", "(due Oct 1)")
const DATE_PAREN_HEAD = "(?!(?:since|due|until|by|open|for|next)\\b)(?:[A-Za-z/]+\\s+){0,2}";

const STATUS_AFTER_NAME = "(?:stale|overdue|due|out of date|missing|not (?:re)?(?:checked|done|repeated|drawn)|never (?:checked|done|drawn))\\b";

// "ALT stale since 2024-04-26 for Fatty liver" -> "ALT": the test's name before a status word, or "" (a
// shared order's pointer names the row that adds it by this when that row has no lead: "↑ ALT", never a
// clipped sentence)
export const nameBeforeStatus = (text) => {
  const m = str(text).match(new RegExp(`^(\\S.{0,22}?)\\s+(?=${STATUS_AFTER_NAME})`, "i"));
  return m ? m[1] : "";
};

export const trimLead = (title, lead) => {
  const t = str(title);
  if (!t || !lead) return t;
  let s = t;
  if (lead.date) {
    // "(Aug 2025)", "(last Mar 2024)", "(CT Aug 22)" equal to the lead's date
    s = s.replace(new RegExp(`\\s*\\(${DATE_PAREN_HEAD}${esc(lead.date)}\\)`, "gi"), "");
  }
  if (lead.kind === "value") {
    const names = labAliases(lead.full).concat(lead.name).map(esc).sort((a, b) => b.length - a.length).join("|");
    const units = lead.units ? `(?:\\s*${esc(lead.units)})?` : "";
    const re = new RegExp(`(^|[\\s(,;+])(?:${names})\\s*:?\\s*${esc(lead.value)}(?![\\d.])${units}(?:\\s*%)?(?:\\s*(?:[↑↓]{1,2}|\\((?:H|L|A|HH|LL)\\)))?`, "i");
    s = s.replace(re, "$1\u0000");
    // "LDL stale since 2024-04-26" under the LDL lead: the test's name before a status word goes too (owner
    // 10-07, V4 graft "shorter findings": the lead already names the test; "Stale since Apr 26, 2024")
    s = s.replace(new RegExp(`^(?:${names})\\s+(?=${STATUS_AFTER_NAME})`, "i"), "");
  }
  if (s === t) return t;
  s = s
    // ", <lead> with type 2 diabetes": the lead was what the comma introduced and the phrase goes on, so
    // the comma goes with it ("CKD stage 3a with type 2 diabetes", never "CKD stage 3a, with ...")
    .replace(/\s*,\s*\u0000\s*(?=[a-z])/g, " ")
    .replace(/\u0000/g, "")
    .replace(/\(\s*\)/g, "")
    .replace(/,(?:\s*,)+/g, ",")
    .replace(/\s*,\s*\+\s*/g, " + ")
    .replace(/\s+([,;:])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s,;:+·—–-]+/, "")
    .replace(/^→\s*/, "")
    .replace(/[\s,;:+·—–-]+$/, "")
    .trim();
  if ((s.match(/[A-Za-z]/g) || []).length < 3) return t;
  return /^[a-z]/.test(s) ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

/* ───────── kind (the level-3 icon) ───────── */

export const recKind = (rec) => {
  const key = str(rec && rec.issue_key).toLowerCase();
  const ot = str(rec && rec.action && rec.action.ordertype).toLowerCase();
  const srcs = (rec && rec.provenance && Array.isArray(rec.provenance.sources)) ? rec.provenance.sources : [];
  const evs = (rec && Array.isArray(rec.evidence)) ? rec.evidence : [];
  if (/vaccin|immuni/.test(ot) || /:(vax|vaccine|vaccines|imm|immunization|immunizations):/.test(`${key}:`)) return "vaccine";
  if ((rec && rec.priority === "risk") || (srcs.length && srcs.every((s) => s && s.list === "risk_factor"))) return "risk";
  if (/:screen(ing)?:/.test(`${key}:`)) return "screening";
  if (/prescri|medicat|\brx\b/.test(ot) || /:med(s)?(:|$)/.test(key)) return "med";
  if (/\blab/.test(ot) || evs.some((e) => e && e.kind === "lab")) return "lab";
  return "other";
};
